import { useEffect, useRef } from 'react';
import { painPoints } from '../../../mocks/landingData';

export default function ProblemSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-slide-up');
          }
        });
      },
      { threshold: 0.1 }
    );

    const section = sectionRef.current;
    if (section) {
      const elements = section.querySelectorAll('.animate-on-scroll');
      elements.forEach((el) => observer.observe(el));
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="relative py-20 md:py-28 bg-warm-white">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="animate-on-scroll opacity-0 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-dark-bronze/30 bg-dark-bronze/10 mb-6">
              <i className="ri-error-warning-line text-dark-bronze text-sm" />
              <span className="text-dark-bronze text-sm font-medium">The Problem</span>
            </div>

            <h2 className="animate-on-scroll opacity-0 font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-midnight mb-6">
              Most people need coaching in the moment,
              <br className="hidden md:block" />
              <span className="gold-gradient-text"> not once a week.</span>
            </h2>

            <p className="animate-on-scroll opacity-0 text-taupe-grey text-base md:text-lg leading-relaxed max-w-3xl mx-auto">
              You do not only need guidance when life is calm. You need it when you are stuck, doubting yourself,
              avoiding action, facing a difficult decision, or trying to push through fear. But most people do not
              have a coach available at 6am, late at night, before a hard conversation, or in the middle of a stressful day.
              TNT Coach AI gives you instant coaching support whenever you need it.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {painPoints.map((point, index) => (
              <div
                key={index}
                className="animate-on-scroll opacity-0 group flex items-start gap-4 p-5 md:p-6 rounded-xl bg-midnight/5 border border-midnight/10 hover:border-metallic-gold/30 hover:bg-midnight/8 transition-all duration-300 card-hover"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="w-10 h-10 rounded-lg bg-midnight/10 flex items-center justify-center flex-shrink-0 group-hover:bg-metallic-gold/20 transition-colors duration-300">
                  <i className={`${point.icon} text-midnight group-hover:text-metallic-gold text-lg transition-colors duration-300`} />
                </div>
                <p className="text-midnight/80 text-sm md:text-base font-medium leading-relaxed pt-2">
                  {point.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}